import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import React from 'react';
import { Table as FlowbiteTable } from 'flowbite-react';

import { TableSkeletonRows } from './TableSkeletonRows';
import Button from '../button/Button';
import { BodyText } from '../typography/Typography';
import './table.css';

/**
 * Table Component with Pagination
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Table content (Table.Head, Table.Body, etc.)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.striped - Whether to apply striped row styling
 * @param {boolean} props.hoverable - Whether to enable row hover effects
 * @param {boolean} props.paginated - Whether to show pagination
 * @param {number} props.currentPage - Current page number for pagination
 * @param {number} props.totalPages - Total number of pages for pagination
 * @param {number} props.itemsPerPage - Number of items per page for pagination
 * @param {Function} props.onPageChange - Function called when page changes
 * @param {Function} props.onItemsPerPageChange - Function called when items per page changes
 * @param {Object} props.rest - Additional props passed to Flowbite Table
 */
const Table = ({
    children,
    className = '',
    striped = false,
    hoverable = false,
    paginated = false,
    currentPage = 1,
    totalPages = 1,
    itemsPerPage = 10,
    onPageChange,
    onItemsPerPageChange,
    ...props
}) => {
    const [inputValue, setInputValue] = React.useState(currentPage);

    // Update input value when currentPage changes
    React.useEffect(() => {
        setInputValue(currentPage);
    }, [currentPage]);

    return (
        <div className="space-y-4">
            <FlowbiteTable
                className={`ui-table ${className}`.trim()}
                striped={striped}
                hoverable={hoverable}
                theme={{
                    root: {
                        base: "w-full text-left text-body text-gray-500 dark:text-gray-400",
                    },
                    head: {
                        // Title case (no uppercase) at the shared body text size.
                        base: "group/head text-body normal-case text-gray-700 dark:text-gray-400",
                    },
                }}
                {...props}
            >
                {children}
            </FlowbiteTable>

            {paginated && totalPages > 1 && (
                <div className="flex justify-end mb-4 mr-4">
                    <div className="flex items-center gap-2">
                        {/* Previous button */}
                        <Button
                            variant="secondary"
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </Button>

                        {/* Page numbers */}
                        {(() => {
                            const pages = [];
                            const maxVisiblePages = 7; // Show max 7 page numbers

                            if (totalPages <= maxVisiblePages) {
                                // Show all pages if we have 7 or fewer
                                for (let i = 1; i <= totalPages; i++) {
                                    pages.push(i);
                                }
                            } else {
                                // Show limited pages with ellipsis
                                if (currentPage <= 4) {
                                    // Near the beginning: show 1, 2, 3, 4, 5, ..., last
                                    for (let i = 1; i <= 5; i++) {
                                        pages.push(i);
                                    }
                                    pages.push('...');
                                    pages.push(totalPages);
                                } else if (currentPage >= totalPages - 3) {
                                    // Near the end: show 1, ..., last-4, last-3, last-2, last-1, last
                                    pages.push(1);
                                    pages.push('...');
                                    for (let i = totalPages - 4; i <= totalPages; i++) {
                                        pages.push(i);
                                    }
                                } else {
                                    // In the middle: show 1, ..., current-1, current, current+1, ..., last
                                    pages.push(1);
                                    pages.push('...');
                                    pages.push(currentPage - 1);
                                    pages.push(currentPage);
                                    pages.push(currentPage + 1);
                                    pages.push('...');
                                    pages.push(totalPages);
                                }
                            }

                            return pages.map((page, index) => (
                                page === '...' ? (
                                    <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                                        ...
                                    </span>
                                ) : (
                                    <Button
                                        key={page}
                                        variant={page === currentPage ? "tertiary" : "secondary"}
                                        onClick={() => onPageChange(page)}
                                    >
                                        {page}
                                    </Button>
                                )
                            ));
                        })()}

                        {/* Next button */}
                        <Button
                            variant="secondary"
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </Button>

                        {/* Page input field */}
                        <div className="flex items-center gap-2">
                            <BodyText>Go to:</BodyText>
                            <input
                                type="number"
                                min="1"
                                max={totalPages}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        const page = parseInt(e.target.value);
                                        if (page >= 1 && page <= totalPages) {
                                            onPageChange(page);
                                        }
                                    }
                                }}
                                className="w-16 px-2 py-1 text-body border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder={currentPage.toString()}
                            />
                            <span className="text-body text-gray-600">of {totalPages}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Table Head Component
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Header content (Table.HeadCell components)
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.rest - Additional props passed to Flowbite Table.Head
 */
const TableHead = ({
    children,
    className = '',
    ...props
}) => {
    return (
        <FlowbiteTable.Head className={className} {...props}>
            {children}
        </FlowbiteTable.Head>
    );
};

/**
 * Table Head Cell Component
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Header cell content
 * @param {string} props.className - Additional CSS classes
 * @param {string|number} props.width - Optional width for the column
 * @param {string} props.align - Text alignment ('left', 'center', 'right')
 * @param {boolean} props.sortable - Whether the column is sortable
 * @param {Function} props.onSort - Function called when sort is triggered
 * @param {string|null} props.sortDirection - Current sort direction ('asc', 'desc', null)
 * @param {Object} props.rest - Additional props passed to Flowbite Table.HeadCell
 */
const TableHeadCell = ({
    children,
    className = '',
    width,
    align = 'left',
    sortable = false,
    onSort,
    sortDirection,
    ...props
}) => {
    const style = width ? { width } : {};

    const handleClick = () => {
        if (sortable && onSort) {
            onSort();
        }
    };

    return (
        <FlowbiteTable.HeadCell
            className={className}
            style={style}
            onClick={handleClick}
            {...props}
        >
            <div className="flex items-center gap-2">
                <BodyText weight="bold" emphasis={false}>{children}</BodyText>
                {sortable && (
                    <span>
                        {sortDirection === 'asc' && <BodyText weight="bold"><ChevronUp size="1em" /></BodyText>}
                        {sortDirection === 'desc' && <BodyText weight="bold"><ChevronDown size="1em" /></BodyText>}
                        {!sortDirection && <BodyText weight="bold"><ChevronsUpDown size="1em" /></BodyText>}
                    </span>
                )}
            </div>
        </FlowbiteTable.HeadCell>
    );
};

/**
 * Table Body Component 
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Table body content (Table.Row components)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.loading - Whether to show skeleton loading rows
 * @param {number} props.skeletonRowCount - Number of skeleton rows when loading
 * @param {string} props.emptyMessage - Message to display when no data is available
 * @param {Object} props.rest - Additional props passed to Flowbite Table.Body
 */
const TableBody = ({
    children,
    className = '',
    loading = false,
    skeletonRowCount = 6,
    emptyMessage = 'No data available',
    ...props
}) => {
    if (loading) {
        return (
            <FlowbiteTable.Body className={className} {...props}>
                <TableSkeletonRows
                    Row={FlowbiteTable.Row}
                    Cell={FlowbiteTable.Cell}
                    rowCount={skeletonRowCount}
                />
            </FlowbiteTable.Body>
        );
    }

    if (!children || (Array.isArray(children) && children.length === 0)) {
        return (
            <FlowbiteTable.Body className={className} {...props}>
                <FlowbiteTable.Row>
                    <FlowbiteTable.Cell colSpan={100} className="text-body text-center py-8 text-gray-500">
                        {emptyMessage}
                    </FlowbiteTable.Cell>
                </FlowbiteTable.Row>
            </FlowbiteTable.Body>
        );
    }

    return (
        <FlowbiteTable.Body className={className} {...props}>
            {children}
        </FlowbiteTable.Body>
    );
};

/**
 * Table Row Component 
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Row content (Table.Cell components)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.selected - Whether the row is selected
 * @param {Function} props.onClick - Click handler for the row
 * @param {Object} props.rest - Additional props passed to Flowbite Table.Row
 */
const TableRow = ({
    children,
    className = '',
    selected = false,
    onClick,
    ...props
}) => {
    return (
        <FlowbiteTable.Row
            className={`${selected ? 'ui-table-row-selected' : ''} ${className}`}
            onClick={onClick}
            data-selected={selected || undefined}
            {...props}
        >
            {children}
        </FlowbiteTable.Row>
    );
};

/**
 * Table Cell Component 
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Cell content
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.rest - Additional props passed to Flowbite Table.Cell
 */
const TableCell = ({
    children,
    className = '',
    ...props
}) => {
    return (
        <FlowbiteTable.Cell className={`text-body ${className}`} {...props}>
            {children}
        </FlowbiteTable.Cell>
    );
};

// Export the main Table component and all sub-components
Table.Head = TableHead;
Table.HeadCell = TableHeadCell;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;

export default Table;
